import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { royaltyRegions, royaltyRulesList } from "../constants/CostingSettingsConstants";

export default function RoyaltyRulesSection({
  royaltyRules,
  isEditing,
  saving,
  canManage,
  onStartEdit,
  onCancelEdit,
  onSave,
  onChange,
  onAddRow,
  onRemoveRow,
}) {
  
  return (
    <div className="bg-white rounded-3xl shadow-lg border border-gray-200 p-10 mb-10">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mb-8">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Royalty Rules</h2>
        </div>

        <div className="flex items-center gap-4">
          {isEditing ? (
            <>
              <button
                onClick={onCancelEdit}
                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-2xl transition-all"
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={saving || !canManage}
                className={`px-6 py-3 font-semibold rounded-2xl flex items-center gap-3 transition-all shadow-lg active:scale-98 ${
                  saving
                    ? "bg-gray-400 text-gray-100 cursor-not-allowed"
                    : "bg-black text-white hover:bg-gray-800"
                }`}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </>
          ) : (
            <button
              onClick={onStartEdit}
              disabled={!canManage}
              className={`px-6 py-3 font-semibold rounded-2xl transition-all shadow-lg active:scale-98 ${
                canManage
                  ? "bg-black text-white hover:bg-gray-800"
                  : "bg-gray-300 text-gray-600 cursor-not-allowed"
              }`}
            >
              Edit
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded-2xl">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Royalty Region</th>
              <th className="px-4 py-3 text-left font-semibold">Royalty Rule</th>
              <th className="px-4 py-3 text-right font-semibold">Royalty %</th>
              <th className="px-4 py-3 text-center font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {(Array.isArray(royaltyRules) ? royaltyRules : []).map((row, index) => (
              <tr key={`royalty-${index}`} className="border-t border-gray-200">
                {/* Royalty Region Dropdown */}
                <td className="px-4 py-3">
                  {isEditing ? (
                    <select
                      value={row.royaltyRegion || ""}
                      onChange={(e) => onChange(index, "royaltyRegion", e.target.value)}
                      className="w-full rounded-xl px-3 py-2 border border-gray-300 focus:border-black focus:ring-1 focus:ring-black bg-white"
                    >
                      <option value="">Select Region</option>
                      {royaltyRegions.map((region) => (
                        <option key={region} value={region}>
                          {region}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="px-3 py-2 text-gray-700 font-medium">
                      {row.royaltyRegion || "—"}
                    </div>
                  )}
                </td>

                {/* Royalty Rule Dropdown */}
                <td className="px-4 py-3">
                  {isEditing ? (
                    <select
                      value={row.royaltyRule || ""}
                      onChange={(e) => onChange(index, "royaltyRule", e.target.value)}
                      className="w-full rounded-xl px-3 py-2 border border-gray-300 focus:border-black focus:ring-1 focus:ring-black bg-white"
                    >
                      <option value="">Select Rule</option>
                      {royaltyRulesList.map((rule) => (
                        <option key={rule} value={rule}>
                          {rule}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="px-3 py-2 text-gray-700 font-medium">
                      {row.royaltyRule || "—"}
                    </div>
                  )}
                </td>

                {/* Royalty Percentage */}
                <td className="px-4 py-3">
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={row.royaltyPercent || ""}
                      onChange={(e) =>
                        onChange(index, "royaltyPercent", e.target.value)
                      }
                      disabled={!isEditing}
                      className={`w-full rounded-xl px-3 py-2 pr-8 border text-right ${
                        isEditing
                          ? "border-gray-300 focus:border-black focus:ring-1 focus:ring-black"
                          : "border-transparent bg-transparent text-gray-700"
                      }`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                      %
                    </span>
                  </div>
                </td>

                <td className="px-4 py-3 text-center">
                  <button
                    type="button"
                    onClick={() => onRemoveRow(index)}
                    disabled={!isEditing}
                    className={`inline-flex items-center justify-center p-2 rounded-lg ${
                      isEditing
                        ? "text-red-600 hover:bg-red-50"
                        : "text-gray-300 cursor-not-allowed"
                    }`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4">
        <button
          type="button"
          onClick={onAddRow}
          disabled={!isEditing}
          className={`px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 ${
            isEditing
              ? "bg-black text-white hover:bg-gray-800"
              : "bg-gray-200 text-gray-500 cursor-not-allowed"
          }`}
        >
          <Plus className="w-4 h-4" />
          Add Row
        </button>
      </div>
    </div>
  );
}